/**
 * TodoChips — iter87.1 · Draft to-do strip.
 *
 * iter87.50 — Split into two labelled rows:
 *   PRE-SEASON PICKS: rank teams, season/pot picks, champion, power up
 *   IN-SEASON PICKS:  matchday boost, rivalries intel
 * The IN-SEASON row (heading + both chips) is hidden entirely when
 * the tournament has no matchday boost enabled. The PRE-SEASON heading
 * hides when its row is empty.
 *
 * Each chip carries a Live / Open / Locked status pill from the shared
 * `chipStatusPill` + `todoStatusFor` primitives so the same status
 * follows the user into every pop-up modal.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Trophy, ListChecks, Crown, Zap, Flag, ChevronRight, ListStart, TrendingUp, AlertTriangle, Swords, Target, Grid3x3 } from "lucide-react";
import { isPlayoffFieldFinal, missedPlayoffsCopy } from "@/lib/playoffField";
import { Baseball, Basketball } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import {
    isGolfFormat, isGolfPickem, isSeasonFormat,
    myPicksForTournament, potPickInfo,
} from "@/lib/tournamentFormat";
import { todoStatusFor, playoffPredictorDisplayStatus, isClubSeason2hPreview, isTournamentComplete } from "@/lib/todoStatus";
import ChipStatusPill from "@/components/onui/ChipStatusPill";
import { api } from "@/lib/api";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";
import { isSquaresTemplate } from "@/components/dashboard/SquaresPanel";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

// iter87.60 → iter87.111 — Positive whitelist of templates that
// actually have a pre-draft team ranking step. Mirrors backend
// `RANKABLE_TEMPLATES` in `tournament_helpers.py`. CBP-style bracketed
// tournaments (world_cup_*, march_madness, olympics, football_playoffs)
// and boost-only templates (`nfl_boost_api`) never consult
// team_preference_ranking, so they stop rendering a phantom "Rank
// Teams" action pill after this gate.
const RANKABLE_TEMPLATES = new Set([
    "custom",
    "premier_league_api",
    "la_liga_api",
    "club_season_api",
    "nfl_season_api",
]);

const BORDER_BY_STATUS = {
    live: "border-volt/60 hover:border-volt",
    open: "border-cyber/40 hover:border-cyber",
    locked: "border-white/[0.08] hover:border-white/20",
    pending: "border-white/[0.08] hover:border-white/20",
};

// Row assignments: which chip key belongs to which section.
const PRE_SEASON_KEYS = new Set(["rank", "pot-picks", "champion", "power", "nwsl-darkhorse", "nfl-darkhorse", "cfb-darkhorse", "mls-darkhorse", "mls-playin", "ucl-darkhorse", "squares"]);
const IN_SEASON_KEYS = new Set(["boost", "rivalries-intel", "mlb-series", "nba-series", "playoff-series-intel", "nwsl-predictor", "mls-series", "mls-series-intel", "mls-knockout-intel", "ucl-advance", "ucl-champions-predictor", "ucl-two-leg-intel", "ucl-final-intel"]);
const NWSL_ROUND_ORDER = ["qf", "sf", "final"];
function fixtureLocked(fx) {
    if (fx.played) return true;
    if (!fx.scheduled_at) return false;
    return new Date(fx.scheduled_at).getTime() <= Date.now();
}

function Chip({ status, icon: Icon, title, action, onClick, testId, hideLockedPill = false, warning }) {
    // iter87.199.51 — Suppress the tile-level LOCKED pill for IN-SEASON
    // chips (Matchday Boost, Gameday Boost, Gameday Picks, Rivalries
    // Intel). Operator directive: these features lock PER-PICK / PER-
    // GAME, so a tile-level "LOCKED" implies "you can't do anything
    // here" — misleading, because the modal is always worth tapping to
    // review this week's grading, prior weeks, and the frozen-at-pick
    // spread per selection. The modal itself keeps rendering LOCKED in
    // its own header where the state is contextually accurate.
    const suppressPill = hideLockedPill && status === "locked";
    return (
        <motion.button
            type="button"
            data-testid={testId}
            onClick={onClick}
            whileHover={{ y: -2, scale: 1.015 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className={`shrink-0 snap-start w-[220px] rounded-2xl border bg-[#121212] p-3 text-left hover:bg-[#161616] ${warning ? "border-amber-500/50 hover:border-amber-400" : (BORDER_BY_STATUS[status] || BORDER_BY_STATUS.locked)}`}
        >
            <div className="flex items-center gap-2 mb-2">
                <span className="w-8 h-8 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-white" />
                </span>
                {!suppressPill && (
                    <ChipStatusPill status={status} testId={`${testId}-status`} className="ml-auto" />
                )}
            </div>
            <div className="font-display font-extrabold text-base uppercase leading-tight tracking-tight">
                {title}
            </div>
            <div className="flex items-center gap-1 text-[12px] text-zinc-400 mt-1 truncate">
                <span className="truncate">{action}</span>
                <ChevronRight className="w-3.5 h-3.5 shrink-0 opacity-70" />
            </div>
            {warning && (
                <div className="flex items-center gap-1 text-[11px] text-amber-400 font-semibold mt-1.5" data-testid={`${testId}-warning`}>
                    <AlertTriangle className="w-3 h-3 shrink-0" />
                    <span className="truncate">{warning}</span>
                </div>
            )}
        </motion.button>
    );
}

// iter87.199.181 — "Thu, Sep 10 · 12:20 AM" style single-line deadline,
// used next to the PRE-SEASON PICKS / IN-SEASON PICKS section labels.
function formatDeadline(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        const datePart = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
        const timePart = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
        return `${datePart} · ${timePart}`;
    } catch { return null; }
}

function buildTodos(ctx) {
    const {
        me, tournament, board, drafts_by_tournament, boost, intel, playoffSeriesIntel, mlbBracket, nbaBracket, nwslBracket,
        squares, onOpenSquares,
        onOpenPotPicks, onOpenMatchdayBoost, onOpenRankTeams,
        onOpenKnockoutPick, onOpenMlbChampsPick, onOpenMlbSeriesPick,
        onOpenNbaChampsPick, onOpenNbaSeriesPick, onOpenPowerPick, onOpenRivalriesIntel,
        onOpenPlayoffSeriesIntel, onOpenNwslChampion, onOpenNwslDarkhorse, onOpenNwslPredictor,
        onOpenNflChampion, onOpenNflDarkhorse,
        onOpenCfbChampion, onOpenCfbDarkhorse,
        onOpenMlsChampion, onOpenMlsDarkhorse, onOpenMlsPlayin, onOpenMlsSeriesPick, mlsBracket,
        onOpenMlsSeriesIntel, onOpenMlsKnockoutIntel,
        onOpenUclChampion, onOpenUclDarkhorse, onOpenUclAdvancePick, onOpenUclChampionsPredictor, uclBracket,
        onOpenUclTwoLegIntel, onOpenUclFinalIntel,
    } = ctx;
    const todos = [];
    if (!me || !tournament) return todos;

    const draftLocked = !!(tournament.id && drafts_by_tournament?.[tournament.id]);
    const isGolf = isGolfFormat(tournament);
    const isGolfPk = isGolfPickem(tournament);
    const isSeason = isSeasonFormat(tournament);
    const isPot = tournament?.pickem?.mode === "pot" || isGolfPk;
    const sctx = { tournament, me, board, boost, draftLocked };

    // 1) Pre-draft team ranking
    // iter87.60 — Only show for templates that ACTUALLY have a rank
    // step. The old gate (template==="custom" OR format in knockout/rr)
    // also fired for world_cup_ko_api / world_cup_api / march_madness
    // brackets whose team assignments never consult ranking, causing a
    // phantom to-do on the CBP Winning production league. Now backed by
    // the RANKABLE_TEMPLATES whitelist (mirrors backend mobile.py gate).
    const showRankChip = RANKABLE_TEMPLATES.has(tournament?.template)
        && tournament?.teams?.length > 0
        && tournament?.pickem?.mode !== "pot";
    if (showRankChip) {
        const status = todoStatusFor("rank", sctx);
        const isKo = tournament?.format === "knockout_only" || tournament?.format === "round_robin";
        const title = isKo ? "Rank your top 5" : "Rank your teams";
        todos.push({
            key: "rank", status, icon: Trophy, title,
            action: status === "locked"
                ? "View your ranking"
                : (isKo ? "Set your top 5" : "Rank your teams"),
            onClick: onOpenRankTeams, testId: "todo-rank-teams",
        });
    }

    // 2) Pot / Golf pick'em pot picks
    if (isPot) {
        let status = todoStatusFor("pot-picks", sctx);
        const pot = potPickInfo(tournament);
        const required = pot.picksRequired;
        const myPicks = myPicksForTournament(me, tournament) || [];
        // iter87.261 (operator-requested) — a pot pick can drop out of
        // the playoff field after being drafted (bubble team, wild
        // card race still fluid at pick time). Flag it same as the
        // My Teams grid.
        const teamsById = Object.fromEntries((tournament?.teams || []).map(tm => [tm.id, tm]));
        const droppedTeams = myPicks.map(tid => teamsById[tid]).filter(tm => tm?.missed_playoffs);
        const fieldFinal = isPlayoffFieldFinal(tournament);
        // iter87.278.2 (user-requested — "the tiles should flip back to
        // live blue that the team drops out of") — once the field is
        // final, a dropped pick can ALWAYS be swapped for a same-pot
        // replacement (see `SeasonPotPickPanel`'s swap flow), even
        // though the rest of the tournament is otherwise locked. The
        // chip flips back to "live" (blue, tappable) to signal that,
        // instead of sitting on "Locked" like everything else.
        if (status === "locked" && fieldFinal && droppedTeams.length > 0) status = "live";
        let action = `Make your picks (${myPicks.length}/${required})`;
        if (status === "locked") {
            // iter87.497 — the 2H live-table preview reuses "locked"
            // status for the chip's gray/non-urgent styling, but the
            // copy must say "opens later", not "closed for good".
            action = isClubSeason2hPreview(tournament)
                ? "Opens after Matchday 19"
                : (myPicks.length > 0 ? "View your picks" : "Picks closed");
        }
        else if (status === "live" && droppedTeams.length > 0) action = "Swap in a replacement";
        else if (status === "open") action = "Change your picks";
        const warning = droppedTeams.length > 0
            ? (fieldFinal
                ? `${droppedTeams.length} pick${droppedTeams.length > 1 ? "s" : ""} missed the field — swap it in`
                : `${droppedTeams.length} pick${droppedTeams.length > 1 ? "s" : ""} outside the field (not final yet)`)
            : null;
        todos.push({
            key: "pot-picks", status, icon: ListChecks,
            title: isGolf ? "Your Golfer Picks" : isFootballPlayoffsTemplate(tournament?.template) ? "Your Playoff Picks" : "Your Season Picks",
            action, onClick: onOpenPotPicks, testId: "todo-pot-picks", warning,
        });
    }

    // 3) Champion Bonus / Champions Pick
    const isNflPot = tournament?.template === "nfl_season_api"
        && (tournament?.pickem?.mode === "pot");
    // iter87.199.37 — SuperDraft retired; the union collapses to isNflPot.
    const potChampBonus = tournament?.pickem?.champions_pick_bonus || 0;
    const seasonHasChampTile = isNflPot && potChampBonus > 0;
    // iter87.216 (user-reported bug) — MLB's Champions Pick is a
    // SEPARATE mechanic (`mlb_champs_by_tournament`, scored by
    // `compute_mlb_champs_points`) from the generic single-slot
    // `knockout_pick_team_id` this tile used to route to for every
    // non-season/non-golf template. That meant clicking this tile on
    // an MLB tournament opened the wrong modal and saved a pick that
    // was NEVER scored. MLB now gets its own branch → `onOpenMlbChampsPick`.
    const isMlbPlayoffs = tournament?.template === "mlb_playoffs_api";
    // iter87.225 — NBA gets its own `nba_champs_by_tournament` branch,
    // same reasoning as the MLB fix above (separate scored mechanic).
    const isNbaPlayoffs = tournament?.template === "nba_playoffs_api";
    // iter87.230 → iter87.437 — NFL/CFB Playoffs both now have their
    // OWN champion mechanic (own branches below) — `isFootballPlayoffs`
    // below still matters for the generic "no Champions Pick" exclusion
    // further down (knockout_only format, not season/golf — without it
    // these fell into the wrong/nonexistent mechanic).
    const isFootballPlayoffs = isFootballPlayoffsTemplate(tournament?.template);
    const isNwslPlayoffs = tournament?.template === "nwsl_playoffs_api";
    // iter87.374 (user-requested) — NFL Playoffs retired its Team Pick
    // Pot/Power Pick in favor of a pure pick'em: Super Bowl Champion
    // (own branch below, up to 2 picks) + Dark Horse Picks (item 4.6
    // below, 2 slots).
    const isNflPlayoffs = tournament?.template === "nfl_playoffs_api";
    // iter87.437 (user-requested — "mirror NFL exactly") — CFB
    // Playoffs followed NFL's lead: National Champion (own branch
    // below, up to 2 picks) + Dark Horse Pick (item 4.7 below, 1 pick —
    // CFB's 12-team field isn't cleanly 2-way groupable like NFL's
    // AFC/NFC, so it's a single pick over the whole non-bye pool).
    const isCfbPlayoffs = tournament?.template === "cfb_playoffs_api";
    // iter87.465.20 (user-approved MLS Playoffs spec) — pure pick'em:
    // Champions Pick (own branch, up to 2 picks) + Dark Horse Pick
    // (item 4.8 below, 1 pick) + Play-In Bonus (item 4.9 below, NEW
    // primitive — 2 conference slots).
    const isMlsPlayoffs = tournament?.template === "mls_playoffs_api";
    const isUclPlayoffs = tournament?.template === "ucl_playoffs_api";
    if (isMlbPlayoffs) {
        const status = todoStatusFor("champion", sctx);
        const wsTeamId = (me.mlb_champs_by_tournament || {})[tournament.id]?.ws;
        const picked = !!wsTeamId;
        const wsTeam = (tournament?.teams || []).find(tm => tm.id === wsTeamId);
        // iter87.246 (user-reported) — shortened from a long explanation
        // ("Pick the World Series champion (+10 pts)") so this tile's
        // width settles near the other tiles' instead of hugging the
        // Chip's max-width.
        let action = "Make your pick";
        if (status === "locked") action = picked ? "View your pick" : "Picks closed";
        else if (status === "open") action = picked ? "Change your pick" : action;
        const warning = (wsTeam?.missed_playoffs)
            ? missedPlayoffsCopy(wsTeam.name, tournament, { locked: status === "locked", subject: "your pick" })
            : null;
        todos.push({
            key: "champion", status, icon: Crown,
            title: "Champions Pick", action, warning,
            onClick: onOpenMlbChampsPick, testId: "todo-champion-pick",
        });
    } else if (isNbaPlayoffs) {
        const status = todoStatusFor("champion", sctx);
        const champTeamId = (me.nba_champs_by_tournament || {})[tournament.id]?.champion;
        const picked = !!champTeamId;
        const champTeam = (tournament?.teams || []).find(tm => tm.id === champTeamId);
        let action = "Make your pick";
        if (status === "locked") action = picked ? "View your pick" : "Picks closed";
        else if (status === "open") action = picked ? "Change your pick" : action;
        const warning = (champTeam?.missed_playoffs)
            ? missedPlayoffsCopy(champTeam.name, tournament, { locked: status === "locked", subject: "your pick" })
            : null;
        todos.push({
            key: "champion", status, icon: Crown,
            title: "Champions Pick", action, warning,
            onClick: onOpenNbaChampsPick, testId: "todo-champion-pick",
        });
    } else if (isNwslPlayoffs) {
        const status = todoStatusFor("champion", sctx);
        const champPicks = (me.nwsl_champion_by_tournament || {})[tournament.id] || [];
        const picked = champPicks.length > 0;
        let action = "Pick your champion(s)";
        if (status === "locked") action = picked ? "View your pick(s)" : "Picks closed";
        else if (status === "open") action = picked ? `Change your pick${champPicks.length > 1 ? "s" : ""}` : action;
        todos.push({
            key: "champion", status, icon: Crown,
            title: "NWSL Champion", action,
            onClick: onOpenNwslChampion, testId: "todo-champion-pick",
        });
    } else if (isNflPlayoffs) {
        const status = todoStatusFor("champion", sctx);
        const champPicks = (me.nfl_champion_picks_by_tournament || {})[tournament.id] || [];
        const picked = champPicks.length > 0;
        let action = "Pick your Super Bowl champ(s)";
        if (status === "locked") action = picked ? "View your pick(s)" : "Picks closed";
        else if (status === "open") action = picked ? `Change your pick${champPicks.length > 1 ? "s" : ""}` : action;
        todos.push({
            key: "champion", status, icon: Crown,
            title: "Super Bowl Champion", action,
            onClick: onOpenNflChampion, testId: "todo-champion-pick",
        });
    } else if (isCfbPlayoffs) {
        const status = todoStatusFor("champion", sctx);
        const champPicks = (me.cfb_champion_picks_by_tournament || {})[tournament.id] || [];
        const picked = champPicks.length > 0;
        let action = "Pick your national champ(s)";
        if (status === "locked") action = picked ? "View your pick(s)" : "Picks closed";
        else if (status === "open") action = picked ? `Change your pick${champPicks.length > 1 ? "s" : ""}` : action;
        todos.push({
            key: "champion", status, icon: Crown,
            title: "National Champion", action,
            onClick: onOpenCfbChampion, testId: "todo-champion-pick",
        });
    } else if (isMlsPlayoffs) {
        const status = todoStatusFor("champion", sctx);
        const champPicks = (me.mls_champion_by_tournament || {})[tournament.id] || [];
        const picked = champPicks.length > 0;
        let action = "Pick your champion(s)";
        if (status === "locked") action = picked ? "View your pick(s)" : "Picks closed";
        else if (status === "open") action = picked ? `Change your pick${champPicks.length > 1 ? "s" : ""}` : action;
        todos.push({
            key: "champion", status, icon: Crown,
            title: "MLS Champion", action,
            onClick: onOpenMlsChampion, testId: "todo-champion-pick",
        });
    } else if (isUclPlayoffs) {
        const status = todoStatusFor("champion", sctx);
        const champPicks = (me.ucl_champion_by_tournament || {})[tournament.id] || [];
        const picked = champPicks.length > 0;
        let action = "Pick your champion(s)";
        if (status === "locked") action = picked ? "View your pick(s)" : "Picks closed";
        else if (status === "open") action = picked ? `Change your pick${champPicks.length > 1 ? "s" : ""}` : action;
        todos.push({
            key: "champion", status, icon: Crown,
            title: "UCL Champion", action,
            onClick: onOpenUclChampion, testId: "todo-champion-pick",
        });
    } else if ((!isSeason && !isGolfPk && !isFootballPlayoffs && !isMlsPlayoffs && !isSquaresTemplate(tournament?.template)) || seasonHasChampTile) {
        const status = todoStatusFor("champion", sctx);
        const picked = !!me.knockout_pick_team_id;
        const title = seasonHasChampTile ? "Champions Pick" : "Champion Bonus";
        const pickVerb = isNflPot
            ? "Pick the team you think posts the best record"
            : "Pick your champion";
        let action = pickVerb;
        if (status === "locked") action = picked ? "View your pick" : "Picks closed";
        else if (status === "open") action = seasonHasChampTile ? "Change your Champions Pick" : "Change your pick";
        todos.push({
            key: "champion", status, icon: Crown,
            title, action,
            onClick: onOpenKnockoutPick, testId: "todo-champion-pick",
        });
    }

    // 4) Power Pick
    if (tournament?.power_pick_enabled) {
        // iter87.405 — the tile-vs-modal "live" override now lives
        // inside `todoStatusFor("power", ...)` itself (the shared
        // single source of truth both this tile AND the modal's
        // status pill read from in `DashboardModalStack.jsx`), so the
        // two can never disagree again.
        const status = todoStatusFor("power", sctx);
        const myRow = board?.find((r) => r.participant_id === me.id);
        const pickTeamId = isGolf ? myRow?.power_pick_team_id : me.power_pick_team_id;
        const picked = !!pickTeamId;
        const powerTeam = (tournament?.teams || []).find(tm => tm.id === pickTeamId);
        const isPotMode = tournament?.pickem?.mode === "pot";
        let action = isGolfPk ? "Pick your power golfer" : "Pick your power team";
        if (status === "locked") {
            action = isClubSeason2hPreview(tournament)
                ? "Opens after Matchday 19"
                : (picked ? "View your power pick" : "Picks closed");
        }
        else if (status === "live" && isPotMode && !picked) action = "Pick a new Power Pick";
        else if (status === "open") action = "Change your power pick";
        const warning = (powerTeam?.missed_playoffs)
            ? missedPlayoffsCopy(powerTeam.name, tournament, { locked: status === "locked", subject: "your power pick" })
            : null;
        todos.push({
            key: "power", status, icon: Zap,
            title: "Power Up", action, warning,
            onClick: onOpenPowerPick, testId: "todo-power-pick",
        });
    }

    // 4.5) Dark Horse Pick (NWSL Playoffs only, pre-season row) — any
    // seed 5-8 team, locked the same instant as Champions Pick.
    if (isNwslPlayoffs) {
        const status = todoStatusFor("nwsl-darkhorse", sctx);
        const dhTeamId = (me.nwsl_darkhorse_by_tournament || {})[tournament.id];
        const picked = !!dhTeamId;
        let action = "Pick a seed 5-8 team";
        if (status === "locked") action = picked ? "View your pick" : "Picks closed";
        else if (status === "open") action = picked ? "Change your pick" : action;
        todos.push({
            key: "nwsl-darkhorse", status, icon: Swords,
            title: "Dark Horse Pick", action,
            onClick: onOpenNwslDarkhorse, testId: "todo-nwsl-darkhorse",
        });
    }

    // 4.6) Dark Horse Picks (NFL Playoffs only, pre-season row) —
    // iter87.374 (user-requested) — 2 SEPARATE picks (one per
    // conference), each a wild-card seed 5-7 team, locked the same
    // instant as Super Bowl Champion.
    if (isNflPlayoffs) {
        const status = todoStatusFor("nfl-darkhorse", sctx);
        const dh = (me.nfl_darkhorse_by_tournament || {})[tournament.id] || {};
        const count = ["AFC", "NFC"].filter((c) => dh[c]).length;
        let action = `Pick your dark horses (${count}/2)`;
        if (status === "locked") action = count > 0 ? "View your picks" : "Picks closed";
        else if (status === "open") action = "Change your picks";
        todos.push({
            key: "nfl-darkhorse", status, icon: Swords,
            title: "Dark Horse Picks", action,
            onClick: onOpenNflDarkhorse, testId: "todo-nfl-darkhorse",
        });
    }

    // 4.7) Dark Horse Pick (CFB Playoffs only, pre-season row) —
    // iter87.437 (user-requested — "mirror NFL exactly") — ONE pick
    // (not NFL's two per-conference). iter87.450 (user-requested):
    // tightened to seed 7-12 only (CFB's field isn't cleanly 2-way
    // groupable like NFL's AFC/NFC), locked the same instant as
    // National Champion.
    if (isCfbPlayoffs) {
        const status = todoStatusFor("cfb-darkhorse", sctx);
        const dhTeamId = (me.cfb_darkhorse_by_tournament || {})[tournament.id];
        const picked = !!dhTeamId;
        let action = "Pick a seed 7-12 team";
        if (status === "locked") action = picked ? "View your pick" : "Picks closed";
        else if (status === "open") action = picked ? "Change your pick" : action;
        todos.push({
            key: "cfb-darkhorse", status, icon: Swords,
            title: "Dark Horse Pick", action,
            onClick: onOpenCfbDarkhorse, testId: "todo-cfb-darkhorse",
        });
    }

    // 4.8) Dark Horse Pick (MLS Playoffs only, pre-season row) — seed
    // 6-9 team (own conference), locked with Champions Pick/Play-In
    // Pick at Wild Card kickoff.
    if (isMlsPlayoffs) {
        const status = todoStatusFor("mls-darkhorse", sctx);
        const dhTeamId = (me.mls_darkhorse_by_tournament || {})[tournament.id];
        const picked = !!dhTeamId;
        let action = "Pick a seed 6-9 team";
        if (status === "locked") action = picked ? "View your pick" : "Picks closed";
        else if (status === "open") action = picked ? "Change your pick" : action;
        todos.push({
            key: "mls-darkhorse", status, icon: Swords,
            title: "Dark Horse Pick", action,
            onClick: onOpenMlsDarkhorse, testId: "todo-mls-darkhorse",
        });
    }

    // 4.9) Play-In Bonus (MLS Playoffs only, pre-season row) — NEW
    // primitive, not part of the in-season Series Predictor. 2
    // independent conference slots, locked with Champions/Dark Horse.
    if (isMlsPlayoffs) {
        const status = todoStatusFor("mls-playin", sctx);
        const pi = (me.mls_playin_by_tournament || {})[tournament.id] || {};
        const count = ["East", "West"].filter((c) => pi[c]).length;
        let action = `Pick your Play-In winners (${count}/2)`;
        if (status === "locked") action = count > 0 ? "View your picks" : "Picks closed";
        else if (status === "open") action = "Change your picks";
        todos.push({
            key: "mls-playin", status, icon: ListChecks,
            title: "Play-In Bonus", action,
            onClick: onOpenMlsPlayin, testId: "todo-mls-playin",
        });
    }

    // 4.8.5) Dark Horse Pick (UCL Playoffs only, pre-season row) —
    // seed 9-24 only (seed 1-8 are byes), locked with UCL Champion at
    // Knockout Round Play-offs kickoff.
    if (isUclPlayoffs) {
        const status = todoStatusFor("ucl-darkhorse", sctx);
        const dhTeamId = (me.ucl_darkhorse_by_tournament || {})[tournament.id];
        const picked = !!dhTeamId;
        let action = "Pick a seed 9-24 team";
        if (status === "locked") action = picked ? "View your pick" : "Picks closed";
        else if (status === "open") action = picked ? "Change your pick" : action;
        todos.push({
            key: "ucl-darkhorse", status, icon: Swords,
            title: "Dark Horse Pick", action,
            onClick: onOpenUclDarkhorse, testId: "todo-ucl-darkhorse",
        });
    }

    // 4.10) MLS Series Predictor (in-season row, Round One only) —
    // iter87.465.22 (user-requested ordering) — pushed BEFORE the
    // Match Predictor tile below so the in-season row reads Series
    // Predictor 1st, Match Predictor 2nd (the order Round One is
    // actually played in). iter87.465.24 (user-requested) — grouped
    // under a "Round of 16 Series" sub-header (16 teams enter Round
    // One, 8 per conference, best-of-3) — reverted the TILE name back
    // to "Series Predictor"/"Match Predictor" (iter87.465.23's "Sweet
    // 16 Series"/"Elite 8 Knockout" rename is superseded by these new
    // sub-headers carrying the bracket-stage branding instead).
    let mlsRound1Complete = false;
    if (isMlsPlayoffs) {
        const rounds = mlsBracket?.rounds || [];
        const r1 = rounds.find((r) => r.key === "round_one");
        const allR1Series = r1?.series || [];
        // iter87.465.25 (testing-agent caught) — require EVERY Round
        // One series to be `final` (none left as `tbd`) before
        // unlocking Playoff Predictor; the old check dropped `tbd`
        // series first, so a bracket with some series still
        // undecided-tbd and the rest final could unlock too early.
        mlsRound1Complete = allR1Series.length > 0 && allR1Series.every((s) => s.status === "final");
        const r1Series = allR1Series.filter((s) => s.status !== "tbd");
        const openSeries = r1Series;
        // iter87.559 (user-reported) — a fully-decided Round One (every
        // series "final", `mlsRound1Complete`) used to fall through to
        // the same "Not Set"/"Bracket TBD" label as a Round One that
        // never got drawn at all. Only the latter should say that —
        // MLS always pre-creates all 8 Round One series as "tbd"
        // placeholders before seeding, so "any series exists" isn't
        // the right test here; use "any NON-tbd series exists" instead
        // (testing-agent caught: the first version regressed the
        // normal pre-Wild-Card state to "View your picks").
        const notSetYet = !rounds.length || !r1 || r1Series.length === 0;
        const myPicks = (me.mls_series_picks_by_tournament || {})[tournament.id] || {};
        const pickableSeries = openSeries.filter((s) => s.status === "open");
        const hasOpenPick = pickableSeries.length > 0;
        const allPicked = hasOpenPick && pickableSeries.every((s) => !!myPicks[s.id]);
        let status, action, statusLabel;
        if (notSetYet && !isTournamentComplete(tournament)) {
            // iter87.561 (user-requested — "pending pill for the gray
            // boxes that meet that [not-yet-seeded] threshold... follow
            // the nfl/cfb logic") — not-yet-drawn now shows the same
            // gray "Pending" pill NFL/CFB's pre-seed state shows,
            // instead of a bare gray box with no pill at all. The
            // DIFFERENT "fully finished" gray box (the `else` branch
            // below, "View your picks") stays locked/no-pill, mirroring
            // NFL/CFB's post-completion treatment (iter87.560).
            // iter87.561.1 (testing-agent caught, iteration_349) —
            // `isTournamentComplete` excluded so a COMPLETE tournament
            // whose bracket endpoint happens to return zero series
            // falls through to the locked "View your picks" branch
            // instead of staying Pending forever.
            status = "pending";
            statusLabel = "Not Set";
            action = "Bracket TBD";
        } else if (hasOpenPick && !allPicked) {
            status = "live";
            action = "Make your picks";
        } else if (hasOpenPick) {
            status = "open";
            action = "Change your picks";
        } else {
            status = "locked";
            action = "View your picks";
        }
        todos.push({
            key: "mls-series", status, icon: Trophy,
            title: "Series Predictor", action, statusLabel,
            onClick: onOpenMlsSeriesPick, testId: "todo-mls-series",
            hideLockedPill: true, // iter87.199.51 — in-season tile
            group: "round_of_16", groupLabel: "Round of 16 Series",
        });
    }

    // 5) Matchday Boost / Gameday Boost / Gameday Picks / Matchday Picks
    // (in-season row) iter87.115 — Boost-only football template
    // (`nfl_boost_api`) renders as "Gameday Picks" —
    // Boost IS the whole tournament there. iter87.122 — the NFL Season
    // Pick'em template (`nfl_season_api`) keeps Boost as a sidecar next
    // to Season Picks / Champions / Power Up, so it renders as
    // **"Gameday Boost"** to stay on-brand with the football family
    // while signaling that this is an in-season boost surface, not the
    // primary picks. iter87.199.169 — Soccer Weekly Spread boost-only
    // (`epl_boost_api`/`la_liga_boost_api`) is ALSO the whole game (same
    // shape as Gameday Picks) — renders as **"Matchday Picks"**, not
    // "Matchday Boost" (that stays reserved for the club-season sidecar
    // surface, e.g. Premier League/La Liga/Champions League club season).
    if (boost?.enabled) {
        const status = todoStatusFor("boost", sctx);
        const st = boost.state;
        const cap = st?.picks_per_round || 2;
        const myPicks = st?.my_picks || [];
        const tpl = tournament?.template;
        const isBoostOnlyFootball = tpl === "nfl_boost_api";
        const isBoostOnlySoccer = tpl === "epl_boost_api" || tpl === "la_liga_boost_api";
        const isSeasonFootball = tpl === "nfl_season_api";
        const isFootball = isBoostOnlyFootball || isSeasonFootball || isFootballPlayoffsTemplate(tpl);
        const isMlsPo = tpl === "mls_playoffs_api";
        const noFixturesLocked = status === "locked" && (st?.fixtures || []).length === 0;
        // iter87.465.22 (testing-agent-caught) — for the Playoff
        // Predictor flavor of this tile (MLS/football playoffs), the
        // raw `status` can read "open" even before the round has any
        // real fixtures/picks (the gray "Pending" display override
        // below is purely cosmetic and never touches this `status`
        // value) — which made the action text say "Change your picks"
        // with nothing to change. Treat "no fixtures yet" the same as
        // the locked case for THIS tile's action copy only.
        const noFixturesYet = (isFootballPlayoffsTemplate(tpl) || isMlsPo) && (st?.fixtures || []).length === 0 && myPicks.length === 0;
        // iter87.560 (user-requested — "pending should be if it's
        // waiting to start... closed/locked is cannot pick because no
        // more picks available") — NFL/CFB Playoff Predictor's
        // `noFixturesYet` gap above fires for BOTH "hasn't been seeded
        // yet" AND "fully finished, no more rounds left" (both read as
        // zero current-round fixtures / zero current-round picks).
        // Split those two with `isTournamentComplete` so a finished
        // bracket drops the "Pending" pill and reads "Tournament
        // complete" instead of "Schedule TBD".
        const footballPlayoffsComplete = isFootballPlayoffsTemplate(tpl) && isTournamentComplete(tournament);
        // iter87.465.24 (user-requested — "until round of 16 series is
        // complete, match predictor and intel stays grayed out...
        // once series predictor last game is final... match predictor
        // goes blue/live") — MLS gets its OWN two-phase gate instead
        // of the blanket "always gray until locked" rule NFL/CFB keep
        // (`playoffPredictorDisplayStatus` below, unchanged for them).
        const mlsGrayedPreRoundOne = isMlsPo && !mlsRound1Complete;
        let action = isFootball ? "See past weeks" : "See past matchdays";
        if (mlsGrayedPreRoundOne) action = "Unlocks after Round of 16";
        else if (footballPlayoffsComplete) action = "Tournament complete";
        else if (noFixturesYet) action = "Schedule TBD";
        else if (status === "live") action = `Make your picks (${myPicks.length}/${cap})`;
        else if (status === "open") action = "Change your picks";
        else if (myPicks.length > 0) action = isFootball ? "View your picks" : "View your boosts";
        else if (noFixturesLocked) action = "Schedule TBD";
        else if ((st?.fixtures || []).length > 0) action = isFootball ? "Week locked" : "Matchday locked";
        const boostTitle = isBoostOnlyFootball
            ? "Gameday Picks"
            : isBoostOnlySoccer
                ? "Matchday Picks"
                : isSeasonFootball
                    ? "Gameday Boost"
                    // iter87.465.24 (user-requested) — "Elite 8
                    // Knockout"/"Road to the Cup" dropped; the new
                    // "Quarterfinal to Championship Rounds" sub-header
                    // carries the bracket-stage branding instead, so
                    // the tile itself goes back to a plain,
                    // predictor-family name. iter87.465.25
                    // (user-requested) — "Match Predictor" renamed to
                    // "Playoff Predictor" to match NFL/CFB's name for
                    // the same tile shape.
                    : isMlsPo
                        ? "Playoff Predictor"
                        : isFootballPlayoffsTemplate(tpl)
                            ? "Playoff Predictor"
                            : "Matchday Boost";
        // iter87.440 (user-reported — "would like all the playoffs
        // games CFB, NWSL and NFL, for the 'Predictor' tile be gray...
        // still can pick it and modal pops up, but it's gray not
        // blue") — Playoff Predictor (the football-playoffs flavor of
        // this chip) never shows the Live/Open blue-or-yellow pill;
        // it's always the neutral gray "Pending" unless the round is
        // fully closed (already gray via "locked"), same tap-through.
        // iter87.465.24 — MLS no longer shares this blanket rule: it's
        // forced gray ONLY before Round One wraps (`mlsGrayedPreRoundOne`
        // above); once Round of 16 is done, Playoff Predictor shows its
        // REAL live/open/locked status like a normal boost tile.
        // iter87.465.25 (testing-agent caught) — EXCEPT right after
        // unlock, before Conf Semis fixtures even exist yet
        // (`noFixturesYet`) — raw `status` can still read "open" there
        // with nothing to act on, so stay gray/pending for that gap too.
        // iter87.559 (user-reported) — NFL/CFB had the same
        // `noFixturesYet` flag computed but never applied to the pill
        // (only to the action text), so a zero-fixture round could
        // show a yellow "Open" pill next to "Schedule TBD" text. Now
        // mirrors MLS's handling of the same gap (still via raw
        // `status` for MLS, not `playoffPredictorDisplayStatus` —
        // that helper's MLS branch is for the modal header only).
        // iter87.560 — `footballPlayoffsComplete` outranks
        // `noFixturesYet`: a finished NFL/CFB bracket renders as
        // "locked" (gray border, pill suppressed by `hideLockedPill`
        // below) instead of the "Pending" gray pill reserved for
        // waiting-on-seeds.
        const tileStatus = mlsGrayedPreRoundOne
            ? "pending"
            : footballPlayoffsComplete
                ? "locked"
                : isMlsPo
                    ? (noFixturesYet ? "pending" : status)
                    : (noFixturesYet ? "pending" : playoffPredictorDisplayStatus(tpl, status));
        todos.push({
            key: "boost", status: tileStatus, icon: Flag,
            title: boostTitle, action,
            statusLabel: noFixturesLocked ? "Schedule TBD" : undefined,
            onClick: onOpenMatchdayBoost, testId: "todo-matchday-boost",
            hideLockedPill: true, // iter87.199.51 — in-season tile
            ...(isMlsPo ? { group: "qf_to_championship", groupLabel: "Quarterfinal to Championship Rounds" } : {}),
        });
    }

    // 5.5) Squares (NFL/CFB single-game board — pre-season row, same
    // "one pick-type surface for the whole tournament" shape as
    // Champion picks). iter87.550.
    if (squares?.enabled && squares.state && squares.state.pro_plus_ok !== false) {
        const sqSt = squares.state;
        const sqMode = sqSt.mode || "manual";
        const sqMyState = sqSt.my_state || "none";
        const sqLocked = !!sqSt.locked;
        // iter87.583 (user-reported) — "live" means "there's still
        // something for you to do here" (same convention as every
        // other to-do chip, e.g. "rank" stays live until submitted —
        // see todoStatus.js). For manual Squares, Save is NOT the
        // finish line — a player sitting at 6/10 saved-but-unclaimed
        // still has work to do, so this must stay "live" with the
        // to-do visible. Only Claimed (or opting out) clears it.
        // Auto-mode has no claim step — admin Confirm is still its
        // own finish line, unchanged.
        const sqLive = sqMode === "auto"
            ? sqMyState === "confirmed"
            : sqMyState !== "dropped" && !sqSt.my_claimed;
        let sqStatus = sqLocked ? "locked" : (sqLive ? "live" : "open");
        let sqAction;
        if (sqLocked) {
            sqAction = sqMode === "auto" ? `Dealt ${sqSt.my_saved_count || 0} box${sqSt.my_saved_count === 1 ? "" : "es"}` : "Board locked";
        } else if (sqSt.my_claimed) {
            sqAction = `${sqSt.my_saved_count || 0} box${sqSt.my_saved_count === 1 ? "" : "es"} claimed`;
        } else if (sqMyState === "confirmed") {
            sqAction = sqMode === "auto" ? "In the lottery" : `${sqSt.my_saved_count || 0} box${sqSt.my_saved_count === 1 ? "" : "es"} picked`;
        } else if (sqMyState === "dropped") {
            sqAction = "Opt back in";
        } else if (sqMode === "auto") {
            sqAction = sqMyState === "opted_in" ? "Awaiting confirm" : "Opt in to join";
        } else if ((sqSt.my_saved_count || 0) > 0) {
            sqAction = `${sqSt.my_saved_count} of ${sqSt.my_allotment || "?"} saved — finish & claim`;
        } else {
            sqAction = "Choose your squares";
        }
        todos.push({
            key: "squares", status: sqStatus, icon: Grid3x3,
            title: sqMode === "auto" ? "Luck of the Draw" : "Squares",
            action: sqAction,
            onClick: onOpenSquares, testId: "todo-squares",
        });
    }

    // 6) Rivalries Intel (in-season row). iter87.50 — Also surface a
    // pre-odds "awaiting_odds" state for eligible tournaments so users
    // know the tile is coming, not missing. iter87.269 — same for
    // "no_fixtures"/outside-window — Pro+ users must always see the
    // tile for an entitled tournament, never nothing.
    // iter87.260 (user-reported) — Suppressed for MLB/NBA Playoffs:
    // "Series Intel" (item 9 below) already covers those tournaments
    // with the playoff-specific Bot Says/Science Says surface, so the
    // weekly regular-season Rivalries Intel chip would be a redundant
    // second tile. Only MLB/NBA today (the only templates with a
    // Series Intel replacement) — any future sport that ships its own
    // Series Intel should get the same carve-out here.
    if (intel?.visible && !isMlbPlayoffs && !isNbaPlayoffs && !isNwslPlayoffs && !isNflPlayoffs && !isCfbPlayoffs && !isMlsPlayoffs) {
        const nSharp = intel.intel?.sharps?.length || 0;
        const nTrap = intel.intel?.trap_picks?.length || 0;
        let action;
        if (intel.hasData) {
            action = (nSharp > 0 || nTrap > 0)
                ? `Top 3 · ${nSharp} sharp · ${nTrap} trap pick${nTrap === 1 ? "" : "s"}`
                : `Top 3 picks by expected value`;
        } else if (intel.outsideWindow) {
            const nextAt = intel.intel?.next_fixture_at;
            action = nextAt
                ? `Next games ${new Date(nextAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })} · check back closer to kickoff`
                : "Check back once games are scheduled";
        } else {
            // awaiting_odds
            action = "Odds land ~1 week before kickoff";
        }
        // iter87.440 (user-reported — "they don't have any info but
        // the tile colour isn't consistent...if there is nothing
        // there, it should be gray") — was hardcoded "open" (yellow)
        // regardless of data; now gray "Pending" until there's
        // actually something to show.
        todos.push({
            key: "rivalries-intel", status: intel.hasData ? "open" : "pending", icon: TrendingUp,
            title: "Rivalries Intel", action,
            onClick: onOpenRivalriesIntel, testId: "todo-rivalries-intel",
            hideLockedPill: true, // iter87.199.51 — in-season tile
        });
    }

    // 7) MLB Series Predictor (in-season row). iter87.217 (user-reported)
    // — retired the standalone always-visible banner card in favor of a
    // proper Draft-grid tile matching every other pick surface.
    if (tournament?.template === "mlb_playoffs_api") {
        const rounds = mlbBracket?.rounds || [];
        const activeRound = rounds.find((r) => (r.series || []).some((s) => s.status === "open"))
            || rounds.find((r) => (r.series || []).some((s) => s.status !== "final"));
        const openSeries = activeRound ? (activeRound.series || []).filter((s) => s.status !== "tbd") : [];
        // iter87.559 (user-reported) — a fully-decided bracket (every
        // series "final") also has no "active" round, which used to
        // read as "never drawn" and show the same "Bracket TBD" label
        // as a bracket that genuinely has zero series yet. Only the
        // latter should say that — distinguish by whether ANY series
        // exists anywhere in the bracket at all.
        const notSetYet = !rounds.length || rounds.every((r) => (r.series || []).length === 0);
        // iter87.265 (user-reported bug) — `hasOpenPick` used to mean
        // "a series in this round is open for picking", which lit the
        // tile up "open" (yellow/done) even when the participant had
        // made ZERO picks yet — yellow is supposed to mean "you've
        // acted, nothing left to do", not "there's something to act
        // on". Mirrors `pot-picks`'s live-vs-open split: blue while any
        // pickable series is still un-picked, yellow only once every
        // pickable series in the round has this participant's pick.
        const myPicks = (me.mlb_series_picks_by_tournament || {})[tournament.id] || {};
        const pickableSeries = openSeries.filter((s) => s.status === "open");
        const hasOpenPick = pickableSeries.length > 0;
        const allPicked = hasOpenPick && pickableSeries.every((s) => !!myPicks[s.id]);
        // iter87.246 (user-reported) — shortened action copy so this
        // tile's width settles near the other tiles' instead of
        // hugging the Chip's max-width (statusLabel pill already says
        // "Not Set", so the notSetYet case doesn't need to repeat it).
        let status, action, statusLabel;
        if (notSetYet && !isTournamentComplete(tournament)) {
            // iter87.561 (user-requested — "pending pill for the gray
            // boxes that meet that [not-yet-seeded] threshold... follow
            // the nfl/cfb logic") — not-yet-drawn shows the same gray
            // "Pending" pill NFL/CFB's pre-seed state shows, instead of
            // a bare gray box with no pill. The "fully finished" gray
            // box (`else` branch below, "View your picks") stays
            // locked/no-pill, mirroring NFL/CFB's post-completion
            // treatment (iter87.560). `isTournamentComplete` excluded
            // (iter87.561.1, testing-agent caught) so a COMPLETE
            // tournament whose bracket endpoint returns zero series
            // falls through to the locked "View your picks" branch.
            status = "pending";
            statusLabel = "Not Set";
            action = "Bracket TBD";
        } else if (hasOpenPick && !allPicked) {
            status = "live";
            action = "Make your picks";
        } else if (hasOpenPick) {
            status = "open";
            action = "Change your picks";
        } else {
            status = "locked";
            action = "View your picks";
        }
        todos.push({
            key: "mlb-series", status, icon: Baseball,
            title: "Series Predictor", action, statusLabel,
            onClick: onOpenMlbSeriesPick, testId: "todo-mlb-series",
            hideLockedPill: true, // iter87.199.51 — in-season tile
        });
    }

    // 8) NBA Series Predictor (in-season row) — sibling of the MLB tile
    // above. Play-In series are excluded from the bracket rounds fed
    // in here (never in `nba_series_picks_by_tournament`), so they
    // never surface as an "open" pick here — Team Pick Pot scores
    // them automatically instead.
    if (isNbaPlayoffs) {
        const rounds = nbaBracket?.rounds || [];
        const activeRound = rounds.find((r) => (r.series || []).some((s) => s.status === "open"))
            || rounds.find((r) => (r.series || []).some((s) => s.status !== "final"));
        const openSeries = activeRound ? (activeRound.series || []).filter((s) => s.status !== "tbd") : [];
        // iter87.559 (user-reported) — same "Bracket TBD" vs "complete"
        // fix as the MLB tile above.
        const notSetYet = !rounds.length || rounds.every((r) => (r.series || []).length === 0);
        // iter87.265 — same live-vs-open fix as the MLB tile above.
        const myPicks = (me.nba_series_picks_by_tournament || {})[tournament.id] || {};
        const pickableSeries = openSeries.filter((s) => s.status === "open");
        const hasOpenPick = pickableSeries.length > 0;
        const allPicked = hasOpenPick && pickableSeries.every((s) => !!myPicks[s.id]);
        let status, action, statusLabel;
        if (notSetYet && !isTournamentComplete(tournament)) {
            // iter87.561 — same not-yet-seeded Pending pill fix as the
            // MLB tile above (excludes a complete tournament, iter87.561.1).
            status = "pending";
            statusLabel = "Not Set";
            action = "Bracket TBD";
        } else if (hasOpenPick && !allPicked) {
            status = "live";
            action = "Make your picks";
        } else if (hasOpenPick) {
            status = "open";
            action = "Change your picks";
        } else {
            status = "locked";
            action = "View your picks";
        }
        todos.push({
            key: "nba-series", status, icon: Basketball,
            title: "Series Predictor", action, statusLabel,
            onClick: onOpenNbaSeriesPick, testId: "todo-nba-series",
            hideLockedPill: true, // iter87.199.51 — in-season tile
        });
    }

    // 8.5) NWSL Match Predictor (in-season row) — sibling of the MLB/
    // NBA Series Predictor tiles above, but per-GAME not per-series:
    // "open" once any fixture in the active round is unpicked.
    if (isNwslPlayoffs) {
        const fixtures = nwslBracket?.fixtures || [];
        const activeRound = NWSL_ROUND_ORDER
            .map((rk) => fixtures.filter((f) => f.round === rk))
            .find((group) => group.length > 0 && group.some((f) => !f.played));
        const openFixtures = (activeRound || []).filter((f) => !fixtureLocked(f));
        const notSetYet = fixtures.length === 0;
        const myPicks = (me.nwsl_predictor_picks_by_tournament || {})[tournament.id] || {};
        const hasOpenPick = openFixtures.length > 0;
        const allPicked = hasOpenPick && openFixtures.every((f) => !!myPicks[f.id]);
        let status, action, statusLabel;
        if (notSetYet && !isTournamentComplete(tournament)) {
            // iter87.561 — same not-yet-seeded Pending pill fix as the
            // MLB/NBA tiles above (excludes a complete tournament, iter87.561.1).
            status = "pending";
            statusLabel = "Not Set";
            action = "Bracket TBD";
        } else if (hasOpenPick && !allPicked) {
            status = "live";
            action = "Make your picks";
        } else if (hasOpenPick) {
            status = "open";
            action = "Change your picks";
        } else {
            status = "locked";
            action = "View your picks";
        }
        todos.push({
            // iter87.441 (user-requested — "instead of match predictor,
            // we probably should call it playoff predictor too, as it's
            // a playoff from a labeling standpoint") — renamed to match
            // CFB/NFL's tile name; scoring mechanics are unchanged
            // (fixed per-game Winner/Decided-by/Goals, not spread/O-U).
            // iter87.558 (user-requested reversal of iter87.440) — now
            // shows its real Open/Live/Locked color, same as MLB/NBA's
            // Series Predictor tiles, instead of a permanent gray
            // "Pending" once open.
            key: "nwsl-predictor", status, icon: Target,
            title: "Playoff Predictor", action, statusLabel,
            onClick: onOpenNwslPredictor, testId: "todo-nwsl-predictor",
            hideLockedPill: true, // iter87.199.51 — in-season tile
        });
    }

    // 8.6) UCL Advance Predictor (in-season row) — one pick per 2-leg
    // tie, spans FOUR rounds (Knockout Round Play-offs, Round of 16,
    // Quarter-finals, Semi-finals) unlike MLS's single-round Series
    // Predictor tile — "active round" is whichever round still has a
    // non-final series, same bracket-wide scan NBA's Series Predictor
    // tile above uses.
    if (isUclPlayoffs) {
        const rounds = (uclBracket?.rounds || []).filter((r) => r.key !== "final");
        const activeRound = rounds.find((r) => (r.series || []).some((s) => s.status === "open"))
            || rounds.find((r) => (r.series || []).some((s) => s.status !== "final"));
        const openSeries = activeRound ? (activeRound.series || []).filter((s) => s.status !== "tbd") : [];
        // iter87.559 (user-reported) — same "Bracket TBD" vs "complete"
        // fix as the MLB/NBA tiles above.
        const notSetYet = !rounds.length || rounds.every((r) => (r.series || []).length === 0);
        const myPicks = (me.ucl_advance_picks_by_tournament || {})[tournament.id] || {};
        const pickableSeries = openSeries.filter((s) => s.status === "open");
        const hasOpenPick = pickableSeries.length > 0;
        const allPicked = hasOpenPick && pickableSeries.every((s) => !!myPicks[s.id]);
        let status, action, statusLabel;
        if (notSetYet && !isTournamentComplete(tournament)) {
            // iter87.561 — same not-yet-seeded Pending pill fix as the
            // MLB/NBA/NWSL tiles above (excludes a complete tournament, iter87.561.1).
            status = "pending";
            statusLabel = "Not Set";
            action = "Bracket TBD";
        } else if (hasOpenPick && !allPicked) {
            status = "live";
            action = "Make your picks";
        } else if (hasOpenPick) {
            status = "open";
            action = "Change your picks";
        } else {
            status = "locked";
            action = "View your picks";
        }
        todos.push({
            key: "ucl-advance", status, icon: Trophy,
            title: "Two-Leg Predictor", action, statusLabel,
            onClick: onOpenUclAdvancePick, testId: "todo-ucl-advance",
            hideLockedPill: true, // iter87.199.51 — in-season tile
            group: "ucl_two_leg", groupLabel: "Knockout to Semifinals",
        });
    }

    // 8.7) UCL Champions Predictor (in-season row, Final only) —
    // sibling of the NWSL Playoff Predictor tile above, but a SINGLE
    // fixture (the Final) with 3 sub-picks (winner/decided-by/goals).
    if (isUclPlayoffs) {
        const finalRound = (uclBracket?.rounds || []).find((r) => r.key === "final");
        const finalTie = (finalRound?.series || [])[0];
        const notSetYet = !finalTie || finalTie.status === "tbd";
        const locked = finalTie?.status !== "open";
        const myPick = (me.ucl_champions_predictor_by_tournament || {})[tournament.id] || {};
        const picked = !!(myPick.winner_team_id || myPick.decided_by || myPick.goals_side);
        let status, action;
        if (notSetYet && !isTournamentComplete(tournament)) {
            // iter87.561 — same not-yet-seeded Pending pill fix as the
            // Series/Two-Leg Predictor tiles above (the Final hasn't
            // been drawn yet, nothing to pick). Excludes a complete
            // tournament (iter87.561.1) — falls to the `locked` branch
            // below instead ("Picks closed").
            status = "pending";
            action = "Final TBD";
        } else if (locked) {
            status = "locked";
            action = picked ? "View your pick" : "Picks closed";
        } else {
            status = picked ? "open" : "live";
            action = picked ? "Change your pick" : "Make your pick";
        }
        // iter87.559 (user-reported reversal) — this used to force
        // "pending" (gray) unless locked, the one tile that never got
        // the iter87.558 NFL/CFB/NWSL reversal applied; now shows its
        // real Live/Open color like every other predictor tile.
        todos.push({
            key: "ucl-champions-predictor", status, icon: Target,
            title: "Champions Predictor", action,
            onClick: onOpenUclChampionsPredictor, testId: "todo-ucl-champions-predictor",
            hideLockedPill: true, // iter87.199.51 — in-season tile
            group: "ucl_championship", groupLabel: "UCL Championship",
        });
    }

    // 8.8) UCL's own two fixed intel tiles (in-season row), same
    // split mechanism as MLS's two tiles above — "Two-Legged Intel"
    // covers Knockout Round Play-offs through Semis as ONE tile,
    // "Champions Intel" is the Final only.
    if (isUclPlayoffs && playoffSeriesIntel?.uclTwoLegIntel?.visible) {
        const ti = playoffSeriesIntel.uclTwoLegIntel;
        const sharpTeam = ti.intel?.sharps?.[0]?.team;
        const trapTeam = ti.intel?.trap_picks?.[0]?.team;
        const action = !ti.hasData ? "No games yet — check back soon"
            : sharpTeam ? `Sharp pick: ${sharpTeam}`
            : trapTeam ? `Trap pick: ${trapTeam}`
            : "This round's games";
        todos.push({
            key: "ucl-two-leg-intel", status: ti.hasData ? "open" : "pending", icon: TrendingUp,
            title: "Two-Legged Intel", action,
            onClick: onOpenUclTwoLegIntel, testId: "todo-ucl-two-leg-intel",
            hideLockedPill: true,
            group: "ucl_two_leg", groupLabel: "Knockout to Semifinals",
        });
    }
    if (isUclPlayoffs && playoffSeriesIntel?.uclFinalIntel?.visible) {
        const fi = playoffSeriesIntel.uclFinalIntel;
        const sharpTeam = fi.intel?.sharps?.[0]?.team;
        const trapTeam = fi.intel?.trap_picks?.[0]?.team;
        const action = !fi.hasData ? "No games yet — check back soon"
            : sharpTeam ? `Sharp pick: ${sharpTeam}`
            : trapTeam ? `Trap pick: ${trapTeam}`
            : "The Final";
        todos.push({
            key: "ucl-final-intel", status: fi.hasData ? "open" : "pending", icon: TrendingUp,
            title: "Champions Intel", action,
            onClick: onOpenUclFinalIntel, testId: "todo-ucl-final-intel",
            hideLockedPill: true,
            group: "ucl_championship", groupLabel: "UCL Championship",
        });
    }

    // 9) Rivalry Intel (in-season row) — MLB/NBA/NWSL/NFL/CFB, sibling
    // of the weekly Rivalries Intel chip above. "Bot says…" Sharp +
    // Trap picks for the currently-active round's series.
    // iter87.364 (user-reported — "I don't see that tile, it should
    // say no games yet / check back a few days before game") — this
    // chip used to be gated on `hasData` alone, so an entitled Pro+
    // user saw NOTHING until Round 1 actually had games scheduled.
    // Now shows as soon as the tournament is eligible+entitled
    // (`visible`), with placeholder copy per `eligibility_state`
    // while `hasData` is false — mirrors the Rivalries Intel chip
    // above, which already does this for the regular-season surface.
    // iter87.365 (user-reported) — renamed from "Series Intel" (NWSL
    // is single-elimination, never a "series") and the copy is now
    // game-specific — names the actual Sharp/Trap pick's team instead
    // of the generic "This round · Sharp + Trap picks".
    // iter87.465.26 (user-requested) — MLS no longer shares this
    // single rotating tile at all (it never sets `playoffSeriesIntel.
    // visible` for MLS — see `usePlayoffSeriesIntel.js`); it gets its
    // OWN pair of fixed tiles below instead.
    if (playoffSeriesIntel?.visible) {
        const sharpTeam = playoffSeriesIntel.intel?.sharps?.[0]?.team;
        const trapTeam = playoffSeriesIntel.intel?.trap_picks?.[0]?.team;
        const roundLabel = playoffSeriesIntel.intel?.round_label || "This round";
        // iter87.561 (user-requested — "apply the same Tournament
        // complete locked state to the Playoff Intel tile for
        // dashboard consistency") — this tile previously had no idea
        // the tournament had finished; a completed bracket kept
        // reading `hasData === false` (no "current round" left) and
        // showed the same gray "Pending"/"No games yet — check back
        // soon" copy as a tournament that genuinely hadn't started.
        const tournamentComplete = isTournamentComplete(tournament);
        let action;
        if (tournamentComplete) {
            action = "Tournament complete";
        } else if (playoffSeriesIntel.hasData) {
            action = sharpTeam ? `Sharp pick: ${sharpTeam}`
                : trapTeam ? `Trap pick: ${trapTeam}`
                // iter87.465.25 (testing-agent caught) — plain "'s"
                // read as "Conference Semifinals's games"; apostrophe
                // alone is correct when the label already ends in "s".
                : `${roundLabel}${roundLabel.endsWith("s") ? "'" : "'s"} games`;
        } else {
            action = "No games yet — check back soon";
        }
        // iter87.440 (user-reported — "if there is nothing there, it
        // should be gray") — was "live" (blue) with no data; now gray
        // "Pending", matching the weekly Rivalries Intel tile's fix.
        // iter87.465.29 (user-requested — "call the nfl cfb nwsl rivalry
        // intel playoff intel to match the MLS naming convention") —
        // single-elimination sports (NFL/CFB/NWSL) now say "Playoff
        // Intel"; MLB/NBA stay "Rivalry Intel" (series-based, untouched).
        // iter87.561 — `tournamentComplete` outranks `hasData`: locked
        // (no pill, gray border via `hideLockedPill`) once finished,
        // same shape as the Playoff Predictor tile's iter87.560 fix.
        todos.push({
            key: "playoff-series-intel", status: tournamentComplete ? "locked" : (playoffSeriesIntel.hasData ? "open" : "pending"), icon: TrendingUp,
            title: (isFootballPlayoffs || isNwslPlayoffs) ? "Playoff Intel" : "Rivalry Intel", action,
            onClick: onOpenPlayoffSeriesIntel, testId: "todo-playoff-series-intel",
            hideLockedPill: true,
        });
    }

    // 9.5) MLS's own two fixed intel tiles (in-season row) —
    // iter87.465.26 (user-requested — "Series Int[el] should only be
    // for the series predictor games (not the wild card playin)...
    // where is the playoff intel (formerly rivalries intel)? it
    // should be grayed out but would follow the nfl/cfb rivalry intel
    // shape for the knockout stage... there should be 2 intel
    // tiles"). Each fetches its OWN backend-scoped doc (see
    // `usePlayoffSeriesIntel.js`/`playoff_series_intel.py`'s
    // `scope=series|knockout`) instead of one tile renaming/regrouping
    // itself off a single rotating "current round" cursor — Round One
    // (wild card explicitly excluded) always renders as "Series
    // Intel" under "Round of 16 Series"; Conf Semis onward always
    // renders as "Playoff Intel" under "Quarterfinal to Championship
    // Rounds", gray/"pending" until real knockout matchups exist —
    // same plain hasData-driven shape NFL/CFB's Rivalry Intel already
    // uses, no extra manual gate.
    if (isMlsPlayoffs && playoffSeriesIntel?.seriesIntel?.visible) {
        const si = playoffSeriesIntel.seriesIntel;
        const sharpTeam = si.intel?.sharps?.[0]?.team;
        const trapTeam = si.intel?.trap_picks?.[0]?.team;
        const action = !si.hasData ? "No games yet — check back soon"
            : sharpTeam ? `Sharp pick: ${sharpTeam}`
            : trapTeam ? `Trap pick: ${trapTeam}`
            : "This round's games";
        todos.push({
            key: "mls-series-intel", status: si.hasData ? "open" : "pending", icon: TrendingUp,
            title: "Series Intel", action,
            onClick: onOpenMlsSeriesIntel, testId: "todo-mls-series-intel",
            hideLockedPill: true,
            group: "round_of_16", groupLabel: "Round of 16 Series",
        });
    }
    if (isMlsPlayoffs && playoffSeriesIntel?.knockoutIntel?.visible) {
        const ki = playoffSeriesIntel.knockoutIntel;
        const sharpTeam = ki.intel?.sharps?.[0]?.team;
        const trapTeam = ki.intel?.trap_picks?.[0]?.team;
        const action = !ki.hasData ? "No games yet — check back soon"
            : sharpTeam ? `Sharp pick: ${sharpTeam}`
            : trapTeam ? `Trap pick: ${trapTeam}`
            : "This round's games";
        todos.push({
            key: "mls-knockout-intel", status: ki.hasData ? "open" : "pending", icon: TrendingUp,
            title: "Playoff Intel", action,
            onClick: onOpenMlsKnockoutIntel, testId: "todo-mls-knockout-intel",
            hideLockedPill: true,
            group: "qf_to_championship", groupLabel: "Quarterfinal to Championship Rounds",
        });
    }

    return todos;
}

// iter87.212.11 — Subtle "more tiles this way" scroll hint. Operator
// report: pre-season tiles (Season Picks / Champions Pick / Power Up)
// overflow horizontally, and some players never scrolled right to find
// Power Up because nothing on screen signals it's there. Purely a
// no-UI-change overlay: a right-edge fade (matches the page's own
// `#0A0A0A` background, so it reads as "content continues under here"
// rather than a new visual element) plus a small nudging chevron —
// both driven by real scroll-position math, so they disappear the
// moment the row is fully scrolled (nothing lingers once discovered).
function useScrollHint(count) {
    const ref = useRef(null);
    const [hasMore, setHasMore] = useState(false);

    const check = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
        // iter87.212.13 — Operator feedback: showing the arrow whenever
        // ANY sliver of the next tile is hidden looked sloppy once a
        // decent chunk of that tile was already peeking into view (that
        // peek is its own "there's more" signal). Only surface the hint
        // when a tile is EFFECTIVELY fully hidden — allow up to ~10px of
        // an imperceptible sliver (snap/rounding noise) before treating
        // it as "still visible enough, no arrow needed".
        const lastTile = el.lastElementChild;
        const tileWidth = lastTile ? lastTile.getBoundingClientRect().width : 0;
        setHasMore(tileWidth > 0 && remaining > 0 && remaining >= tileWidth - 10);
    }, []);

    useEffect(() => {
        check();
        const el = ref.current;
        if (!el) return;
        el.addEventListener("scroll", check, { passive: true });
        window.addEventListener("resize", check);
        return () => {
            el.removeEventListener("scroll", check);
            window.removeEventListener("resize", check);
        };
    }, [check, count]);

    return { ref, hasMore };
}

// iter87.465.24 (user-requested — sub-headers within the In-Season row
// for templates with "varying formats", first needed for MLS's
// Round of 16 Series vs Quarterfinal-to-Championship split) — the
// scroller itself is now its own component so both the plain flat row
// AND each grouped sub-row can reuse it. `useScrollHint` is called
// once per `ChipScroller` instance, same hook-call rules as before.
function ChipScroller({ todos, testId }) {
    const { ref: scrollerRef, hasMore } = useScrollHint(todos.length);
    return (
        // iter87.212.13 — `-mx-4` moved onto this wrapper (was only on
        // the scroller) so the absolutely-positioned hint's `right-0`
        // lines up with the scroller's true bled-out right edge
        // instead of the narrower padded container — that mismatch
        // was why the arrow sat short of the edge with tile color
        // still visible past it.
        <div className="relative -mx-4">
            <div
                ref={scrollerRef}
                data-testid={`${testId}-scroller`}
                className="flex items-stretch gap-3 overflow-x-auto snap-x snap-mandatory px-4 py-2 no-scrollbar"
                style={{ scrollbarWidth: "none" }}
            >
                {todos.map(({ key, ...rest }) => (
                    <Chip key={key} {...rest} />
                ))}
            </div>
            {hasMore && (
                <div
                    data-testid={`${testId}-more-hint`}
                    aria-hidden="true"
                    className="pointer-events-none absolute right-0 top-2 bottom-2 w-12 flex items-center justify-end rounded-r-2xl"
                    style={{ background: "linear-gradient(to left, #0A0A0A 15%, transparent)" }}
                >
                    <motion.div
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                        className="w-6 h-6 mr-1 rounded-full bg-white/[0.08] border border-white/15 flex items-center justify-center"
                    >
                        <ChevronRight className="w-3.5 h-3.5 text-cyber" />
                    </motion.div>
                </div>
            )}
        </div>
    );
}

function SectionRow({ label, lockInfo, todos, testId }) {
    if (todos.length === 0) return null;
    // iter87.465.24 — Any todo carrying a `group`/`groupLabel` (MLS
    // only today) splits this section into labeled sub-rows, each
    // with its own `ChipScroller`. No `group` anywhere (every other
    // template) renders exactly the single flat row as before — same
    // markup/testids, zero regression.
    const groupKeys = [];
    const groupMap = new Map();
    for (const t of todos) {
        const g = t.group || null;
        if (!groupMap.has(g)) { groupMap.set(g, []); groupKeys.push(g); }
        groupMap.get(g).push(t);
    }
    const isGrouped = groupKeys.length > 1 || groupKeys[0] !== null;
    return (
        <div className="space-y-1.5" data-testid={testId}>
            <p className="px-1 text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold flex items-center gap-1.5 flex-wrap">
                {label}
                {lockInfo && (
                    <span data-testid={`${testId}-lock-info`} className="text-zinc-400 tracking-normal normal-case font-semibold">
                        · {lockInfo}
                    </span>
                )}
            </p>
            {!isGrouped ? (
                <ChipScroller todos={todos} testId={testId} />
            ) : (
                <div className="space-y-3">
                    {groupKeys.map((g) => {
                        const items = groupMap.get(g);
                        const groupLabel = items[0]?.groupLabel;
                        const groupTestId = `${testId}-${g || "ungrouped"}`;
                        return (
                            <div key={g || "ungrouped"} className="space-y-1">
                                {groupLabel && (
                                    <p data-testid={`${groupTestId}-label`} className="px-1 text-[9px] uppercase tracking-[0.16em] text-zinc-400 font-semibold">
                                        {groupLabel}
                                    </p>
                                )}
                                <ChipScroller todos={items} testId={groupTestId} />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// iter87.503 (user-requested) — Single glowing to-do pill next to the
// "DRAFT" header, replacing the old plain "N live"/"All caught up"
// text. Tapping it pops a list of just this tournament's currently
// LIVE (actionable) tiles — same `todos` array + `status === "live"`
// filter the header text already used for its count, so the pill can
// never drift from the tile colors underneath it. Picking a row fires
// that tile's own `onClick` (already wired to the right modal opener
// by `buildTodos`), so e.g. tapping "Power Up" here opens the exact
// same Power Pick modal the tile itself would.
function DraftTodoPill({ todos }) {
    const [open, setOpen] = useState(false);
    const live = todos.filter((t) => t.status === "live");
    if (live.length === 0) {
        return (
            <p className="text-[10px] text-zinc-500" data-testid="draft-todo-pill-zero">
                All caught up
            </p>
        );
    }
    const count = live.length;
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    data-testid="draft-todo-pill"
                    aria-label={`${count} to-do${count === 1 ? "" : "s"} waiting on you`}
                    className="relative inline-flex items-center gap-1 h-6 px-2.5 rounded-full border text-[10px] font-black uppercase tracking-wider leading-none transition-transform active:scale-95 select-none touch-manipulation"
                    style={{ background: "rgba(255, 179, 71, 0.12)", borderColor: "rgba(255, 179, 71, 0.5)", color: "#ffb347" }}
                >
                    {count} to-do{count === 1 ? "" : "s"}
                    <span aria-hidden data-testid="draft-todo-pill-ping" className="absolute -top-0.5 -right-0.5 flex h-2 w-2 pointer-events-none">
                        <span className="absolute inline-flex h-full w-full rounded-full opacity-70 animate-ping" style={{ background: "#ffb347" }} />
                        <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: "#ffb347" }} />
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                data-testid="draft-todo-popover"
                align="end"
                side="bottom"
                sideOffset={6}
                collisionPadding={12}
                className="w-[260px] p-3 rounded-lg border border-zinc-700 bg-[#0f0f10] text-white shadow-2xl overflow-y-auto max-h-[min(70vh,var(--radix-popover-content-available-height,70vh))]"
            >
                <p className="font-display font-black text-xs uppercase tracking-tight mb-2">
                    Next Up
                </p>
                <ul className="space-y-0.5">
                    {live.map((t) => (
                        <li key={t.key} className="border-b border-dashed border-zinc-800 last:border-b-0">
                            <button
                                type="button"
                                data-testid={`draft-todo-pill-item-${t.key}`}
                                onClick={() => { setOpen(false); t.onClick?.(); }}
                                className="w-full flex items-center gap-2.5 py-1.5 text-left rounded transition-colors hover:bg-white/[0.04] active:bg-white/[0.08]"
                            >
                                <span aria-hidden className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-[3px]" style={{ background: "#ffb347" }} />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-bold text-white leading-tight truncate">{t.title}</p>
                                    <p className="text-[10px] text-zinc-500 truncate">{t.action}</p>
                                </div>
                                <ChevronRight className="w-3 h-3 text-zinc-600 shrink-0" />
                            </button>
                        </li>
                    ))}
                </ul>
            </PopoverContent>
        </Popover>
    );
}

// iter87.199.181 — Pre-Season Picks (Your Season Picks / Champions
// Pick / Power Up) all lock at the SAME instant per operator spec —
// the season's first kickoff (`tournament.round_started.group`).
// Reuses Matchday Boost's round-1 fixture timestamps rather than a
// second fixtures fetch. Only rendered for fixture-based season pot
// formats where we actually have a real timestamp — golf pot pick'em
// locks on tee time, not a fixture list surfaced here, so it's
// intentionally left blank rather than guessed.
function preSeasonLockInfo(tournament, boost) {
    const isPot = tournament?.pickem?.mode === "pot";
    if (!isPot || !boost?.enabled) return null;
    if (tournament?.round_started?.group) return "Closed";
    const times = (boost.state?.fixtures || []).map((f) => f.scheduled_at).filter(Boolean).sort();
    if (times.length === 0) return null;
    return `Picks lock on ${formatDeadline(times[0])}`;
}

// iter87.199.181 — In-Season Picks (Matchday/Gameday Boost) lock
// PER-GAME as each fixture kicks off, so "closes on" would be
// misleading — this instead says when the round STARTS locking (its
// earliest still-open fixture), or that the round is fully closed.
// iter87.199.181 — Copy tweak per operator: "Next {round} picks lock
// on {day, date, time}" instead of "start locking on" — this naturally
// updates on its own as fixtures start (each render re-filters
// `openFixtures`, so the earliest surviving one just becomes the next
// "Next ... lock on" target), no extra state needed.
function inSeasonLockInfo(tournament, boost) {
    const st = boost?.state;
    if (!boost?.enabled || !st || st.current_round == null) return null;
    const tpl = tournament?.template;
    const isFootball = tpl === "nfl_boost_api" || tpl === "nfl_season_api" || isFootballPlayoffsTemplate(tpl);
    const roundLabel = isFootball ? `Week ${st.current_round}` : `MD ${st.current_round}`;
    const openFixtures = (st.fixtures || []).filter((f) => !f.started);
    if (openFixtures.length === 0) return `${roundLabel} fixtures closed`;
    const times = openFixtures.map((f) => f.scheduled_at).filter(Boolean).sort();
    if (times.length === 0) return null;
    return `Next ${roundLabel} picks lock on ${formatDeadline(times[0])}`;
}

export default function TodoChips(props) {
    const { tournament, boost } = props;
    const isMlbPlayoffs = tournament?.template === "mlb_playoffs_api";
    const isNbaPlayoffs = tournament?.template === "nba_playoffs_api";
    const isNwslPlayoffs = tournament?.template === "nwsl_playoffs_api";
    const isMlsPlayoffs = tournament?.template === "mls_playoffs_api";
    const isUclPlayoffs = tournament?.template === "ucl_playoffs_api";
    const [mlbBracket, setMlbBracket] = useState(null);
    const [nbaBracket, setNbaBracket] = useState(null);
    const [nwslBracket, setNwslBracket] = useState(null);
    const [mlsBracket, setMlsBracket] = useState(null);
    const [uclBracket, setUclBracket] = useState(null);
    useEffect(() => {
        if (!isMlbPlayoffs || !tournament?.id) return;
        const load = () => api.getMlbBracket(tournament.id).then(setMlbBracket).catch(() => {});
        load();
        const iv = setInterval(load, 60000);
        return () => clearInterval(iv);
    }, [isMlbPlayoffs, tournament?.id]);
    useEffect(() => {
        if (!isNbaPlayoffs || !tournament?.id) return;
        const load = () => api.getNbaBracket(tournament.id).then(setNbaBracket).catch(() => {});
        load();
        const iv = setInterval(load, 60000);
        return () => clearInterval(iv);
    }, [isNbaPlayoffs, tournament?.id]);
    useEffect(() => {
        if (!isNwslPlayoffs || !tournament?.id) return;
        const load = () => api.getNwslBracket(tournament.id).then(setNwslBracket).catch(() => {});
        load();
        const iv = setInterval(load, 60000);
        return () => clearInterval(iv);
    }, [isNwslPlayoffs, tournament?.id]);
    useEffect(() => {
        if (!isMlsPlayoffs || !tournament?.id) return;
        const load = () => api.getMlsBracket(tournament.id).then(setMlsBracket).catch(() => {});
        load();
        const iv = setInterval(load, 60000);
        return () => clearInterval(iv);
    }, [isMlsPlayoffs, tournament?.id]);
    useEffect(() => {
        if (!isUclPlayoffs || !tournament?.id) return;
        const load = () => api.getUclBracket(tournament.id).then(setUclBracket).catch(() => {});
        load();
        const iv = setInterval(load, 60000);
        return () => clearInterval(iv);
    }, [isUclPlayoffs, tournament?.id]);
    const todos = buildTodos({ ...props, mlbBracket, nbaBracket, nwslBracket, mlsBracket, uclBracket });
    if (todos.length === 0) return null;

    const preSeason = todos.filter((t) => PRE_SEASON_KEYS.has(t.key));
    const inSeason = todos.filter((t) => IN_SEASON_KEYS.has(t.key));

    // iter87.50 — Hide the whole In-Season section (heading + both
    // chips) when the tournament has no Matchday Boost enabled, even
    // if Rivalries Intel would otherwise qualify. Per operator spec.
    // iter87.217 — MLB Playoffs also qualifies (Series Predictor tile),
    // even though it has no Matchday Boost.
    // iter87.225 — NBA Playoffs, sibling of the MLB carve-out above.
    // iter87.361 — NWSL Playoffs, sibling (Match Predictor tile).
    const showInSeason = (!!boost?.enabled || isMlbPlayoffs || isNbaPlayoffs || isNwslPlayoffs || isMlsPlayoffs || isUclPlayoffs) && inSeason.length > 0;

    // iter87.199.67 — Section header briefly swapped to "Picks" for
    // Pot Pick'em / golf pick'em tournaments (no draft mechanic) as a
    // belt-and-braces guard against a template-leak bug. iter87.199.152
    // — Operator reverted this: the landing page's "DRAFT · CHEER ·
    // DOMINATE" pillar copy is the anchor, so this section heading
    // stays "Draft" for every tournament regardless of pot/draft
    // mechanic, even though the tile copy underneath correctly still
    // says "picks" for no-draft formats.
    const sectionHeading = "Draft";

    return (
        <section data-testid="todo-chips" aria-label={`${sectionHeading} to-dos`} className="space-y-3">
            <div className="flex items-baseline justify-between px-1">
                <p className="text-base sm:text-lg uppercase tracking-[0.18em] text-white font-bold flex items-center gap-2">
                    <ListStart className="w-5 h-5" /> {sectionHeading}
                </p>
                <DraftTodoPill todos={todos} />
            </div>
            <SectionRow
                label="Pre-Season Picks"
                lockInfo={preSeasonLockInfo(tournament, boost)}
                todos={preSeason}
                testId="todo-chips-pre-season"
            />
            {showInSeason && (
                <SectionRow
                    label="In-Season Picks"
                    lockInfo={inSeasonLockInfo(tournament, boost)}
                    todos={inSeason}
                    testId="todo-chips-in-season"
                />
            )}
        </section>
    );
}
